import { zodResolver } from '@hookform/resolvers/zod';
import { Info } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { ReactNode } from 'react';
import { Accept, FileWithPath } from 'react-dropzone';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FileDropzone } from '../../atoms/file-dropzone/file-dropzone';
import { LoadingButton } from '../../atoms/loading-button/loading-button';
import { DialogActions as DialogActionsInterface } from '../../molecules/dialog';
import FileList from '../../molecules/file-list/file-list';

import { useUploadDialogValidationSchema } from './schema/upload-dialog-validation-schema';

export type UploadDialogSubmitResult = void | { errorMessage: string };

interface UploadDialogProps extends DialogActionsInterface {
  handleDownload?: () => void;
  onSubmit: (file: File[]) => Promise<UploadDialogSubmitResult>;
  isSubmitting?: boolean;
  message?: ReactNode;
  title: string;
  templateId?: string;
  importId?: string;
  maxFilesNumber?: number;
  maxFileSize?: number;
  fileDropzoneTitle?: string;
  fileDropzoneDescription?: string;
  submitButtonText?: string;
  filesAcceptTypes?: Accept;
}

interface FormValues {
  files: FileWithPath[];
}
const defaultValues = {
  files: [],
};

function UploadDialog({
  open,
  onCancel,
  onSubmit,
  handleDownload,
  message,
  title,
  templateId,
  importId,
  isSubmitting,
  filesAcceptTypes,
  maxFileSize,
  submitButtonText,
  maxFilesNumber,
  fileDropzoneTitle,
  fileDropzoneDescription,
}: UploadDialogProps) {
  const { t } = useTranslation(['validation', 'common']);

  const validationSchema = useUploadDialogValidationSchema();

  const { control, clearErrors, handleSubmit, watch, setValue, setError, reset } =
    useForm<FormValues>({
      defaultValues,
      mode: 'onChange',
      resolver: zodResolver(validationSchema),
    });

  const onConfirm = async (data: FormValues) => {
    if (!data.files || data.files?.length <= 0) return;
    const result = await onSubmit(data.files);

    if (result && result?.errorMessage) {
      setError('files', {
        type: 'manual',
        message: result.errorMessage,
      });
    }
  };

  const files = watch('files');

  return (
    <Dialog
      onClose={onCancel}
      open={open}
      PaperProps={{
        sx: {
          width: '100%',
        },
      }}
      TransitionProps={{
        onExited: () => reset(defaultValues),
      }}>
      <DialogTitle
        sx={{
          alignItems: 'center',
          display: 'flex',
        }}>
        <Info color="info" fontSize="large" />
        <Typography color="inherit" sx={{ ml: 2 }} variant="inherit">
          {title}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>{message}</Box>
        <Controller
          control={control}
          name="files"
          render={({ fieldState: { error }, field: { value } }) => (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FileDropzone
                accept={filesAcceptTypes as Accept}
                maxFiles={maxFilesNumber}
                maxSize={maxFileSize}
                title={fileDropzoneTitle}
                description={fileDropzoneDescription}
                onDrop={(acceptedFiles, fileRejections) => {
                  clearErrors('files');

                  if (
                    fileRejections?.length > 0 &&
                    acceptedFiles?.length <= 0 &&
                    fileRejections?.[0]?.errors?.[0]?.code === 'file-invalid-type'
                  ) {
                    setError('files', {
                      message: t('common:incorrectFileType'),
                    });
                    return;
                  }
                  if (
                    fileRejections?.length > 0 &&
                    fileRejections?.[0]?.errors?.[0]?.code === 'too-many-files'
                  ) {
                    setError('files', {
                      message: t('common:maxFilesNumberError', {
                        limit: maxFilesNumber,
                      }),
                    });
                    return;
                  }
                  if (!maxFilesNumber || maxFilesNumber > acceptedFiles?.length + files?.length) {
                    setValue('files', [...files, ...acceptedFiles], { shouldValidate: true });
                  } else {
                    setError('files', {
                      message: t('common:maxFilesNumberError', {
                        limit: maxFilesNumber,
                      }),
                    });
                  }
                  if (
                    maxFilesNumber === 1 &&
                    fileRejections?.[0]?.errors?.[0]?.code !== 'too-many-files'
                  ) {
                    setValue('files', acceptedFiles, { shouldValidate: true });
                  }
                }}
              />
              <Box>
                {value?.length > 0 ? (
                  <FileList
                    files={value ?? []}
                    onDeleteFile={index => {
                      const newFiles = value.filter((_, i) => i !== index);
                      setValue('files', newFiles);
                    }}
                  />
                ) : null}
                {error?.message ? (
                  <Typography color="error.main" variant="subtitle2">
                    {error.message}
                  </Typography>
                ) : null}
              </Box>
            </Box>
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={onCancel} variant="outlined">
          {t('common:button.cancel')}
        </Button>
        {handleDownload ? (
          <Button color="primary" onClick={handleDownload} variant="contained" id={templateId}>
            {t('common:button.downloadTemplate')}
          </Button>
        ) : null}
        <LoadingButton
          color="primary"
          loading={isSubmitting || false}
          onClick={handleSubmit(onConfirm)}
          disabled={!files || files?.length <= 0}
          id={importId}
          variant="contained">
          {submitButtonText ?? t('common:button.add')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

export default UploadDialog;
